// ThemeSyncer.jsx
// Bridges AuthProvider preferences with ThemeProvider
// Must be rendered inside both providers

import { useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from './useTheme';
import { logger } from '../lib/logger';

export default function ThemeSyncer() {
  const { preferences, ready } = useAuth();
  const { setTheme, theme } = useTheme();
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Only sync once preferences are loaded and auth is ready
    if (!ready || !preferences?.theme) return;

    // Convert from API format (DARK/LIGHT/SYSTEM) to theme format (dark/light/system)
    const preferredTheme = preferences.theme.toLowerCase();

    // Only update if different from current theme setting
    if (preferredTheme !== theme && !hasInitialized.current) {
      logger.log('[ThemeSyncer] Applying user preference:', preferredTheme);
      setTheme(preferredTheme);
      hasInitialized.current = true;
    }
  }, [preferences?.theme, ready, setTheme, theme]);

  // Also sync when preference changes after initial load
  useEffect(() => {
    if (!hasInitialized.current) return;
    if (!preferences?.theme) return;

    const preferredTheme = preferences.theme.toLowerCase();
    if (preferredTheme !== theme) {
      logger.log('[ThemeSyncer] User preference changed:', preferredTheme);
      setTheme(preferredTheme);
    }
    // setTheme is stable from useTheme; resolvedTheme is intentionally
    // not depended on — this effect only reacts to remote preference changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences?.theme, theme, setTheme]);

  return null; // This component doesn't render anything
}
